// Modal do zapytania o produkt

import React, { FC, useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { useSelector } from 'store';
import { IProduct } from 'api/types';
import { usePostProductAskQuestion } from 'api';
import { useNotifications } from 'hooks';
import { Modal, Input, Button, FormElement, ErrorMessage } from 'components/controls';

import styles from 'theme/components/containers/AskProductModal/AskProductModal.module.scss';

interface IProps {
  product: IProduct;
  open: boolean;
  onClose: () => void;
}

const AskProductModal: FC<IProps> = ({ product, open, onClose }) => {
  const { t } = useTranslation();
  const { overloginUserId } = useSelector((state) => state.auth);
  const { showSuccessMessage, showErrorMessage } = useNotifications();

  // OPEN_PROFILE to użytkownik który nie loguje się przez "zaloguj się jako" (brak overloginUserId)
  const isOpenProfile = !overloginUserId;

  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ message?: string; email?: string; phone?: string }>({});

  const { mutate: sendQuestion, isLoading } = usePostProductAskQuestion(product.id, {
    onSuccess: () => {
      showSuccessMessage(t('Zapytanie zostało wysłane'));
      handleClose();
    },
    onError: (error: any) => {
      showErrorMessage(error?.response?.data?.message || t('Wystąpił błąd podczas wysyłania zapytania'));
    }
  });

  const handleClose = () => {
    setMessage('');
    setEmail('');
    setPhone('');
    setErrors({});
    onClose();
  };

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue.trim());
  };

  const validatePhone = (phoneValue: string): boolean => {
    const phoneRegex = /^\+?[1-9][0-9]{7,14}$/;
    return phoneRegex.test(phoneValue.trim().replace(/\s/g, ''));
  };

  const validate = (): boolean => {
    const newErrors: { message?: string; email?: string; phone?: string } = {};

    if (!message.trim()) {
      newErrors.message = t('Treść zapytania jest wymagana');
    }

    if (isOpenProfile) {
      const emailTrimmed = email.trim();
      const phoneTrimmed = phone.trim();

      if (!emailTrimmed && !phoneTrimmed) {
        newErrors.email = t('Musi być podany adres e-mail lub numer telefonu');
        newErrors.phone = t('Musi być podany adres e-mail lub numer telefonu');
      } else {
        if (emailTrimmed && !validateEmail(emailTrimmed)) {
          newErrors.email = t('Niepoprawny adres email');
        }
        if (phoneTrimmed && !validatePhone(phoneTrimmed)) {
          newErrors.phone = t('Niepoprawny numer telefonu');
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    sendQuestion({
      message: message.trim(),
      ...(isOpenProfile && email.trim() ? { email: email.trim() } : {}),
      ...(isOpenProfile && phone.trim() ? { phone: phone.trim() } : {})
    });
  };

  const productName = useMemo(
    () =>
      product?.product_basic_short_info?.title || product?.product_basic_full_info?.title || '',
    [product]
  );
  const productIndex = useMemo(
    () =>
      product?.product_basic_short_info?.index || product?.product_basic_full_info?.index || '',
    [product]
  );

  const modalTitle = productName
    ? `${t('Zapytaj o produkt:')} ${productName}`
    : t('Zapytaj o produkt:');

  return (
    <Modal open={open} onClose={handleClose} title={modalTitle}>
      <div className={classnames(styles.confirmation, 'StylePath-Components-Containers-AskProductModal')}>
        {productIndex && (
          <div className={styles.productIndex}>
            <Trans>INDEX</Trans>: {productIndex}
          </div>
        )}

        <FormElement>
          <label>{t('Treść zapytania')}</label>
          <textarea
            className={classnames({ [styles.error]: !!errors.message })}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (errors.message) {
                setErrors({ ...errors, message: undefined });
              }
            }}
            placeholder={t('Wpisz swoje zapytanie')}
            rows={6}
          />
          {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
        </FormElement>

        {isOpenProfile && (
          <>
            <FormElement>
              <label>{t('Adres e-mail')}</label>
              <Input
                type="text"
                value={email}
                onChange={(value) => {
                  setEmail(value);
                  if (errors.email) {
                    setErrors({ ...errors, email: undefined });
                  }
                }}
                placeholder={t('test@example.com')}
                error={errors.email}
              />
            </FormElement>

            <FormElement>
              <label>{t('Numer telefonu')}</label>
              <Input
                type="text"
                value={phone}
                onChange={(value) => {
                  setPhone(value);
                  if (errors.phone) {
                    setErrors({ ...errors, phone: undefined });
                  }
                }}
                placeholder={t('+48123456789')}
                error={errors.phone}
              />
            </FormElement>
          </>
        )}

        <div className={styles.actions}>
          <Button color="secondary" ghost onClick={handleClose} disabled={isLoading}>
            <Trans>Anuluj</Trans>
          </Button>
          <Button onClick={handleSubmit} loading={isLoading}>
            <Trans>Wyślij zapytanie</Trans>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AskProductModal;

